import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, X, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Search,
  ClipboardList, Trash2, Truck, Upload,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Input, Select, Textarea, Spinner, EmptyState } from "@/components/ui";

// =============================================================================
// Crane library — T-436
// =============================================================================
// The asset that compounds: every lift study a crane company sends us has a
// chart in it. Capture each one and in a year we hold something nobody else in
// telco has.
//
// THREE LEVELS, and the middle one is the whole point:
//   model   Liebherr LTM 1060-3.1
//   config  counterweight x outrigger spread x boom mode   <- a chart lives HERE
//   points  capacity by boom length x radius (x variant)
// The same LTM 1060-3.1 does 8.5t @ 10m on Walcha's 4.3t counterweight and
// 16.0t @ 10m on Park Ridge's 12.8t. A chart attached to a MODEL would quietly
// hand a rigger the wrong number.
//
// Charts land as DRAFT and a competent person verifies against the source
// document before any lift plan can use them — a mistyped cell is a wrong
// capacity, which is a hazard, not a typo.
// =============================================================================

type Model = { crane_model_id: string; make: string; model: string; crane_type: string; capacity_class_t: number | null; notes: string | null };
type Config = {
  crane_config_id: string; crane_model_id: string; config_name: string; setup_mode: string | null;
  counterweight_t: number | null; outrigger_width_m: number | null; outrigger_length_m: number | null;
  main_winch_capacity_kg: number | null; chart_wind_limit_ms: number | null;
  variant_axis: string | null; variant_label: string | null;
  source_document: string | null; status: string; verified_at: string | null;
  chart_basis: string; machine_serial: string | null;
  cells?: number;
};

export default function CraneLibraryScreen() {
  const user = useRequireAuth();
  const canEdit = hasRole(user, "manager");
  const qc = useQueryClient();
  const [openConfig, setOpenConfig] = useState<string | null>(null);
  const [addingModel, setAddingModel] = useState(false);
  const [addingConfigFor, setAddingConfigFor] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const { data: models = [], isLoading } = useQuery({
    queryKey: ["crane_models"],
    queryFn: async () => {
      const { data, error } = await supabase.from("crane_models")
        .select("*").eq("is_active", true).order("make").order("model");
      if (error) throw error;
      return (data ?? []) as Model[];
    },
  });

  const { data: configs = [] } = useQuery({
    queryKey: ["crane_configs"],
    queryFn: async () => {
      const { data, error } = await supabase.from("crane_configs")
        .select("*, crane_chart_points(count)").order("config_name");
      if (error) throw error;
      return (data ?? []).map((c: any) => ({
        ...c, cells: c.crane_chart_points?.[0]?.count ?? 0,
      })) as Config[];
    },
  });

  const ql = q.trim().toLowerCase();
  const shown = models.filter(m => !ql || `${m.make} ${m.model}`.toLowerCase().includes(ql));
  const cfgOf = (m: Model) => configs.filter(c => c.crane_model_id === m.crane_model_id);

  // Three states, because "what can I actually use right now" was impossible to
  // read off a flat list where most entries are empty shells.
  const ready   = shown.filter(m => cfgOf(m).some(c => c.status === "verified" && (c.cells ?? 0) > 0));
  // Any model with a configuration gets the full card, chart or not. It used to
  // need cells > 0, which meant a freshly-created empty config dropped into the
  // "awaiting a chart" strip below — and that strip only lists model names, so
  // the config was saved but invisible and unclickable (James, 01/08). The card
  // already handles the empty case with a "no chart loaded" pill.
  const toCheck = shown.filter(m => !ready.includes(m) && cfgOf(m).length > 0);
  const waiting = shown.filter(m => !ready.includes(m) && !toCheck.includes(m));

  const totalCells = configs.reduce((s, c) => s + (c.cells ?? 0), 0);
  const verifiedCount = configs.filter(c => c.status === "verified" && (c.cells ?? 0) > 0).length;
  const chartedCount = configs.filter(c => (c.cells ?? 0) > 0).length;

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
          <h1 className="text-2xl font-semibold tracking-tight">Crane library</h1>
          <p className="text-text-muted text-[13px] mt-1 max-w-[560px]">
            Load charts we hold, by crane and configuration. Every lift study a crane company
            sends us has a chart in it — capture it once and every future lift plan does the
            maths off real numbers.
          </p>
        </div>
        {canEdit && <Button icon={Plus} onClick={() => setAddingModel(true)}>Add crane</Button>}
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"/>
        <Input className="pl-9" placeholder="Search make or model…" value={q} onChange={e => setQ(e.target.value)}/>
      </div>

      {/* Honest state of the library at a glance */}
      <div className="grid grid-cols-3 gap-3">
        <StatBox n={verifiedCount} label="ready to use" tone={verifiedCount > 0 ? "ok" : "muted"}/>
        <StatBox n={chartedCount - verifiedCount} label="need verifying" tone={chartedCount - verifiedCount > 0 ? "warn" : "muted"}/>
        <StatBox n={configs.filter(c => (c.cells ?? 0) === 0).length + waiting.length} label="awaiting a chart" tone="muted"/>
      </div>
      {verifiedCount === 0 && (
        <div className="bg-warn/10 border border-warn/40 rounded-md px-4 py-3 text-[12px] text-warn">
          <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/>
          <b>No chart is usable yet.</b> Lift plans can't calculate until at least one configuration is
          verified — load a chart, check it against the source, then verify it.
          {totalCells > 0 && <> There {chartedCount === 1 ? "is 1 chart" : `are ${chartedCount} charts`} loaded
            ({totalCells} cells) waiting on that check.</>}
        </div>
      )}

      {isLoading && <div className="flex justify-center py-10"><Spinner size={18}/></div>}
      {!isLoading && shown.length === 0 && (
        <EmptyState icon={Truck} title="No cranes yet"
          description="Add the crane, then paste its load chart from the lift study."/>
      )}

      {/* Cranes that hold a chart come first — the empty shells collapse below */}
      {[...ready, ...toCheck].length > 0 && (
        <div className="text-[11px] uppercase tracking-wider text-text-muted">
          Cranes we've set up · {[...ready, ...toCheck].length}
        </div>
      )}
      <div className="space-y-4">
        {[...ready, ...toCheck].map(m => {
          const mine = cfgOf(m);
          return (
            <Card key={m.crane_model_id}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[15px] font-semibold">{m.make} {m.model}</span>
                      {m.capacity_class_t && <Pill label={`${m.capacity_class_t}t class`} tone="default" small/>}
                      <Pill label={m.crane_type} tone="default" small/>
                    </div>
                    {m.notes && <div className="text-[12px] text-text-muted mt-1 max-w-[620px]">{m.notes}</div>}
                  </div>
                  {canEdit && (
                    <Button size="sm" variant="secondary" icon={Plus}
                      onClick={() => setAddingConfigFor(m.crane_model_id)}>Add configuration</Button>
                  )}
                </div>

                <div className="mt-3 space-y-1.5">
                  {mine.length === 0 && (
                    <div className="text-[12px] text-text-dim italic">
                      No configurations yet — a chart belongs to a setup (counterweight + outriggers), not the crane.
                    </div>
                  )}
                  {mine.map(c => (
                    <button key={c.crane_config_id} type="button"
                      onClick={() => setOpenConfig(c.crane_config_id)}
                      className="w-full text-left rounded-md border border-line/60 hover:border-accent/50 bg-ink px-3 py-2.5 transition">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[13px] flex-1 min-w-[200px]">{c.config_name}</span>
                        <Pill label={c.chart_basis === "machine" ? "machine chart" : "generic"}
                          tone={c.chart_basis === "machine" ? "ok" : "notice"} small/>
                        {c.cells === 0
                          ? <Pill label="no chart loaded" tone="warning" small/>
                          : <span className="text-[11px] text-text-muted font-mono">{c.cells} cells</span>}
                        {c.status === "verified"
                          ? <Pill label="verified" tone="ok" small/>
                          : <Pill label="draft — not usable" tone="warning" small/>}
                      </div>
                      {c.source_document && (
                        <div className="text-[11px] text-text-dim mt-1 truncate">{c.source_document}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {waiting.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">
            Awaiting a chart · {waiting.length}
          </div>
          <Card>
            <div className="divide-y divide-line/40">
              {waiting.map(m => (
                <div key={m.crane_model_id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap">
                  <span className="text-[13px] flex-1 min-w-[200px]">{m.make} {m.model}</span>
                  {m.capacity_class_t && (
                    <span className="text-[11px] text-text-muted font-mono">{m.capacity_class_t}t</span>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="secondary" icon={Plus}
                      onClick={() => setAddingConfigFor(m.crane_model_id)}>Add configuration</Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
          <div className="text-[11px] text-text-dim mt-1.5">
            Models with no chart yet. Add a configuration, then paste its grid from the manufacturer
            sheet or a crane company's study.
          </div>
        </div>
      )}

      {openConfig && (
        <ConfigDrawer configId={openConfig} canEdit={canEdit} isDirector={hasRole(user, "director")}
          onClose={() => setOpenConfig(null)}
          onChanged={() => { qc.invalidateQueries({ queryKey: ["crane_configs"] }); }}/>
      )}
      {addingModel && <AddModelModal onClose={() => setAddingModel(false)}
        onSaved={() => { setAddingModel(false); qc.invalidateQueries({ queryKey: ["crane_models"] }); }}/>}
      {addingConfigFor && <AddConfigModal modelId={addingConfigFor} onClose={() => setAddingConfigFor(null)}
        onSaved={() => { setAddingConfigFor(null); qc.invalidateQueries({ queryKey: ["crane_configs"] }); }}/>}
    </div>
  );
}

// =============================================================================
// Config drawer — the chart itself, its ingestion, and the verify gate
// =============================================================================
function ConfigDrawer({ configId, canEdit, isDirector, onClose, onChanged }: {
  configId: string; canEdit: boolean; isDirector: boolean; onClose: () => void; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [pasting, setPasting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: cfg } = useQuery({
    queryKey: ["crane_config", configId],
    queryFn: async () => {
      const { data, error } = await supabase.from("crane_configs")
        .select("*, crane_models(make, model)").eq("crane_config_id", configId).single();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: points = [] } = useQuery({
    queryKey: ["crane_points", configId],
    queryFn: async () => {
      const { data, error } = await supabase.from("crane_chart_points")
        .select("boom_length_m, radius_m, variant, capacity_kg")
        .eq("crane_config_id", configId)
        .order("radius_m");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  async function verify() {
    if (!window.confirm(
      "Verify this chart?\n\nYou're confirming the numbers match the source document. Once verified, lift plans will calculate capacities from it.",
    )) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.schema("app").rpc("verify_crane_config", { p_config_id: configId });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["crane_config", configId] });
    onChanged();
  }

  // Switching basis resets verification (mig 254 trigger) — say so plainly.
  async function switchBasis(next: "generic" | "machine") {
    const serial = next === "machine"
      ? window.prompt("Machine serial or crane ID from the hire company's chart (optional):", cfg?.machine_serial ?? "")
      : null;
    if (next === "machine" && serial === null) return;
    if (!window.confirm(
      `Mark this chart as ${next === "machine" ? "the actual machine's chart" : "a generic manufacturer chart"}?\n\nThis clears the verification — someone will need to check and verify it again.`,
    )) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.from("crane_configs")
      .update({ chart_basis: next, machine_serial: next === "machine" ? (serial || null) : null })
      .eq("crane_config_id", configId);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["crane_config", configId] });
    onChanged();
  }

  // Delete the whole configuration. Guarded on two things: a verified chart is
  // referenced by approved lift plans, and any chart with cells in it took real
  // effort to load — so make the destructive path the awkward one.
  async function deleteConfig() {
    if (cfg?.status === "verified") {
      setErr("This configuration is verified and lift plans may reference it. Switch it back to draft first if you really need it gone.");
      return;
    }
    const label = cfg?.config_name ?? "this configuration";
    if (!window.confirm(
      `Delete "${label}"?\n\n${points.length > 0
        ? `Its ${points.length} chart cells go with it. `
        : ""}This can't be undone.`,
    )) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.from("crane_configs").delete().eq("crane_config_id", configId);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onChanged();
    onClose();
  }

  async function clearChart() {
    if (!window.confirm("Delete every cell in this chart? The configuration stays.")) return;
    setBusy(true);
    const { error } = await supabase.from("crane_chart_points").delete().eq("crane_config_id", configId);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["crane_points", configId] });
    onChanged();
  }

  // Remove ONE variant section without touching the rest. Before this, a typo
  // in a variant label (a "6/46/..." that should have been "46/46/...") meant
  // Clear-and-repaste-everything — on a 14-section chart that's the whole
  // job again (James, 10/09, the Beechmont LTM 1120 entry).
  async function deleteVariant(v: string) {
    const n = points.filter(p => (p.variant ?? "") === v).length;
    if (!window.confirm(
      `Remove the "${v || "(unlabelled)"}" section?\n\nIts ${n} cells are deleted. The other sections stay.`,
    )) return;
    setBusy(true); setErr(null);
    const { error } = await supabase.from("crane_chart_points").delete()
      .eq("crane_config_id", configId).eq("variant", v);
    setBusy(false);
    if (error) { setErr(error.message); return; }
    qc.invalidateQueries({ queryKey: ["crane_points", configId] });
    onChanged();
  }

  // Pivot the flat point list into the grid a crane chart actually looks like.
  const booms = Array.from(new Set(points.map(p => Number(p.boom_length_m)))).sort((a, b) => a - b);
  const radii = Array.from(new Set(points.map(p => Number(p.radius_m)))).sort((a, b) => a - b);
  const variants = Array.from(new Set(points.map(p => p.variant ?? ""))).sort();
  const cell = (b: number, r: number, v: string) =>
    points.find(p => Number(p.boom_length_m) === b && Number(p.radius_m) === r && (p.variant ?? "") === v);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[860px] my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-line flex items-start justify-between gap-3">
          <div>
            <div className="text-[12px] text-text-muted">{cfg?.crane_models?.make} {cfg?.crane_models?.model}</div>
            <div className="text-[15px] font-semibold mt-0.5">{cfg?.config_name}</div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {cfg?.status === "verified"
                ? <Pill label={`verified ${cfg.verified_at ? new Date(cfg.verified_at).toLocaleDateString("en-AU") : ""}`} tone="ok" small/>
                : <Pill label="draft — lift plans can't use this yet" tone="warning" small/>}
              <span className="text-[11px] text-text-muted font-mono">{points.length} cells</span>
            </div>
          </div>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            <Meta label="Counterweight" value={cfg?.counterweight_t != null ? `${cfg.counterweight_t} t` : "—"}/>
            <Meta label="Outriggers" value={cfg?.outrigger_width_m ? `${cfg.outrigger_width_m} × ${cfg.outrigger_length_m} m` : "—"}/>
            <Meta label="Main winch" value={cfg?.main_winch_capacity_kg ? `${(cfg.main_winch_capacity_kg / 1000).toFixed(1)} t` : "—"}/>
            <Meta label="Chart wind" value={cfg?.chart_wind_limit_ms ? `${cfg.chart_wind_limit_ms} m/s` : "—"}/>
          </div>

          {/* Basis — planning charts and lift charts are different things. */}
          <div className={`rounded-md border px-3 py-2.5 ${cfg?.chart_basis === "machine"
            ? "bg-ok/5 border-ok/30" : "bg-warn/5 border-warn/30"}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px]">
                {cfg?.chart_basis === "machine" ? (
                  <span className="text-ok">
                    Machine chart{cfg?.machine_serial ? ` · ${cfg.machine_serial}` : ""} — for the actual hired crane.
                  </span>
                ) : (
                  <span className="text-text-muted">
                    <b className="text-text">Generic chart</b> — manufacturer published. Fine for planning and
                    quoting; get the hired machine's own chart before anyone lifts.
                  </span>
                )}
              </div>
              {canEdit && (
                <Button size="sm" variant="secondary" disabled={busy}
                  onClick={() => void switchBasis(cfg?.chart_basis === "machine" ? "generic" : "machine")}>
                  Mark as {cfg?.chart_basis === "machine" ? "generic" : "machine chart"}
                </Button>
              )}
            </div>
          </div>

          {cfg?.source_document && (
            <div className="bg-ink rounded-md border border-line/50 px-3 py-2 text-[12px] text-text-muted">
              <span className="text-text-dim">Source: </span>{cfg.source_document}
            </div>
          )}

          {points.length === 0 ? (
            <div className="border border-dashed border-line rounded-md p-6 text-center">
              <div className="text-[13px] text-text-muted">No chart loaded yet.</div>
              {canEdit && (
                <div className="mt-3 flex gap-2 justify-center flex-wrap">
                  <Button icon={ClipboardList} onClick={() => setPasting(true)}>Paste the load chart</Button>
                  <Button variant="secondary" icon={Upload} onClick={() => setImporting(true)}>Import a pack file</Button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-text-muted">
                  Capacity (kg) · radius × boom length
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" icon={ClipboardList} onClick={() => setPasting(true)}>Paste more</Button>
                    <Button size="sm" variant="secondary" icon={Upload} onClick={() => setImporting(true)}>Import pack</Button>
                    <Button size="sm" variant="secondary" icon={Trash2} onClick={clearChart} disabled={busy}>Clear</Button>
                  </div>
                )}
              </div>
              {variants.map(v => (
                <div key={v} className="mb-4">
                  {(v || variants.length > 1) && (
                    <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                      <div className="text-[11px] text-accent">{cfg?.variant_label ?? "Variant"}: {v || "(unlabelled)"}</div>
                      {/* One bad section shouldn't cost the other thirteen. */}
                      {canEdit && variants.length > 1 && (
                        <button type="button" disabled={busy} onClick={() => void deleteVariant(v)}
                          className="text-[11px] text-text-dim hover:text-danger flex items-center gap-1 transition-colors">
                          <Trash2 size={11}/> remove this section
                        </button>
                      )}
                    </div>
                  )}
                  <div className="text-[10px] text-text-dim mb-1 font-mono">
                    boom length (m) →&nbsp;&nbsp;·&nbsp;&nbsp;↓ radius (m)
                  </div>
                  {/* Ruled + centred: this grid is read cell-by-cell against the
                      source chart before someone signs it off, so it has to be
                      as legible as the printed chart it's checked against. */}
                  {/* w-auto, not w-full — with one boom column a full-width table
                      strands the value miles from its radius label. */}
                  <div className="overflow-x-auto border border-line rounded inline-block max-w-full align-top">
                    <table className="text-[11px] font-mono w-auto border-collapse">
                      <thead>
                        <tr>
                          <th className="px-2 py-1.5 text-center text-text-muted bg-ink-raised w-[54px]
                                         border-b border-r border-line sticky left-0 z-10">m</th>
                          {booms.map(b => (
                            <th key={b} className="px-3 py-1.5 text-center text-accent bg-ink-raised min-w-[78px]
                                                   border-b border-r border-line last:border-r-0 whitespace-nowrap">
                              {b}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {radii.map((r, ri) => (
                          <tr key={r} className={ri % 2 ? "bg-ink/40" : ""}>
                            <td className={`px-2 py-1.5 text-center text-text-muted border-b border-r border-line
                                            sticky left-0 z-10 w-[54px] ${ri % 2 ? "bg-ink" : "bg-ink-card"}`}>
                              {r}
                            </td>
                            {booms.map(b => {
                              const c = cell(b, r, v);
                              return (
                                <td key={b}
                                  className={`px-3 py-1.5 text-center border-b border-r border-line last:border-r-0
                                              min-w-[78px] ${c ? "text-text" : "text-text-dim"}`}>
                                  {c ? c.capacity_kg.toLocaleString() : "·"}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[10px] text-text-dim mt-1">
                    {radii.length} radii × {booms.length} boom {booms.length === 1 ? "length" : "lengths"} ·
                    capacities in kg · “·” means not charted at that combination
                  </div>
                </div>
              ))}
            </div>
          )}

          {cfg?.status !== "verified" && points.length > 0 && canEdit && (
            <div className="bg-warn/10 border border-warn/40 rounded-md p-3">
              <div className="text-[12px] text-warn flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0"/>
                <span>
                  Check every figure against the source chart before verifying. A mistyped cell becomes a
                  wrong capacity on a real lift.
                </span>
              </div>
              <Button className="mt-3" icon={ShieldCheck} onClick={verify} disabled={busy}>
                {busy ? <Loader2 size={14} className="animate-spin"/> : "I've checked it — verify this chart"}
              </Button>
            </div>
          )}
          {cfg?.status === "verified" && (
            <div className="flex items-center gap-2 text-[12px] text-ok">
              <CheckCircle2 size={14}/> Verified — lift plans can calculate from this chart.
            </div>
          )}

          {canEdit && cfg?.status !== "verified" && (
            <div className="pt-3 border-t border-line/50 flex items-center justify-between gap-3 flex-wrap">
              <span className="text-[11px] text-text-dim">
                Created in error, or a duplicate? Remove it so the library stays honest.
              </span>
              <Button size="sm" variant="danger" icon={Trash2} onClick={deleteConfig} disabled={busy}>
                Delete configuration
              </Button>
            </div>
          )}
        </div>
      </div>

      {pasting && (
        <PasteChartModal configId={configId} variantAxis={cfg?.variant_axis ?? null} variantLabel={cfg?.variant_label ?? null}
          capacityClassT={cfg?.crane_models?.capacity_class_t ?? null}
          onClose={() => setPasting(false)}
          onSaved={() => {
            setPasting(false);
            qc.invalidateQueries({ queryKey: ["crane_points", configId] });
            onChanged();
          }}/>
      )}
      {importing && (
        <ImportPackModal configId={configId} variantAxis={cfg?.variant_axis ?? null}
          capacityClassT={cfg?.crane_models?.capacity_class_t ?? null}
          onClose={() => setImporting(false)}
          onSaved={() => {
            setImporting(false);
            qc.invalidateQueries({ queryKey: ["crane_points", configId] });
            onChanged();
          }}/>
      )}
    </div>
  );
}

function StatBox({ n, label, tone }: { n: number; label: string; tone: "ok" | "warn" | "muted" }) {
  const cls = tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : "text-text-muted";
  return (
    <div className="bg-ink rounded-md border border-line/50 px-3 py-2.5">
      <div className={`text-[20px] ${cls}`}>{n}</div>
      <div className="text-[11px] text-text-muted">{label}</div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-dim font-mono">{label}</div>
      <div className="text-text mt-0.5">{value}</div>
    </div>
  );
}

// =============================================================================
// Paste ingestion — the fast path (T-420 bulk-paste pattern, applied to charts)
// =============================================================================
// Crane charts are a grid: boom lengths across the top, radii down the side.
// Copy it out of the PDF, paste, preview, commit. Parsing is deliberately
// forgiving (tabs, commas, multiple spaces) because PDF copy-paste is messy —
// but the PREVIEW is the point: you see exactly what will be stored before it
// is stored.
// =============================================================================
function PasteChartModal({ configId, variantAxis, variantLabel, capacityClassT, onClose, onSaved }: {
  configId: string; variantAxis: string | null; variantLabel: string | null;
  capacityClassT: number | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [raw, setRaw] = useState("");
  const [variant, setVariant] = useState("");
  const [units, setUnits] = useState<"t" | "kg">("t");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsed = parseChart(raw, units);

  // Units trap: a kg file pasted as tonnes silently multiplies everything by
  // 1000 and the grid still looks plausible at a glance. Compare the biggest
  // cell against the crane's own rating and refuse to commit if it's absurd.
  const maxKg = parsed.rows.length ? Math.max(...parsed.rows.map(r => r.kg)) : 0;
  const ratedKg = capacityClassT ? capacityClassT * 1000 : null;
  const wildlyHigh = !!ratedKg && maxKg > ratedKg * 1.6;
  const wildlyLow = !!ratedKg && maxKg > 0 && maxKg < ratedKg * 0.05;

  async function commit() {
    if (parsed.rows.length === 0 || wildlyHigh) return;
    setBusy(true); setErr(null);
    const payload = parsed.rows.map(r => ({
      crane_config_id: configId,
      boom_length_m: r.boom,
      radius_m: r.radius,
      variant: variantAxis ? variant.trim() : "",
      capacity_kg: r.kg,
    }));
    // Chunked upsert — a full chart can be several hundred cells.
    for (let i = 0; i < payload.length; i += 200) {
      const { error } = await supabase.from("crane_chart_points")
        .upsert(payload.slice(i, i + 200), { onConflict: "crane_config_id,boom_length_m,radius_m,variant" });
      if (error) { setBusy(false); setErr(error.message); return; }
    }
    setBusy(false);
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[720px] my-6" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">Paste the load chart</div>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] text-text-muted leading-relaxed">
            Copy the grid straight out of the lift study — <b className="text-text">boom lengths across the top row</b>,
            <b className="text-text"> radii down the first column</b>. Tabs, commas or spaces all work. Blank cells stay blank.
            Check the preview before you commit.
          </div>
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-[12px] text-text-muted">Capacities are in
              <Select value={units} onChange={e => setUnits(e.target.value as any)}>
                <option value="t">tonnes</option>
                <option value="kg">kilograms</option>
              </Select>
            </label>
            {variantAxis && (
              <label className="block text-[12px] text-text-muted">{variantLabel ?? "Variant"}
                <Input value={variant} onChange={e => setVariant(e.target.value)} placeholder="e.g. 0"/>
              </label>
            )}
          </div>

          <Textarea rows={8} value={raw} onChange={e => setRaw(e.target.value)}
            placeholder={"\t27.7\t34.6\t41.6\n10.0\t10.4\t9.6\t8.5\n11.0\t9.1\t8.2\t7.7"}
            className="font-mono text-[12px]"/>

          {raw.trim() && (
            <div className="border border-line rounded-md p-3">
              {parsed.error ? (
                <div className="text-[12px] text-danger">{parsed.error}</div>
              ) : (
                <>
                  <div className="text-[12px] text-ok flex items-center gap-1.5">
                    <CheckCircle2 size={13}/> {parsed.rows.length} cells across {parsed.booms.length} boom lengths
                  </div>
                  <div className="text-[11px] text-text-muted mt-1 font-mono">
                    booms {parsed.booms.join(", ")} m · radius {parsed.minR}–{parsed.maxR} m ·
                    capacity {Math.min(...parsed.rows.map(r => r.kg)).toLocaleString()}–{Math.max(...parsed.rows.map(r => r.kg)).toLocaleString()} kg
                  </div>
                  {parsed.warn && <div className="text-[11px] text-warn mt-1.5">{parsed.warn}</div>}
                  {wildlyHigh && (
                    <div className="mt-2 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                      <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/>
                      <b>Units look wrong.</b> The biggest capacity here is {(maxKg / 1000).toLocaleString()} t
                      on a {capacityClassT} t crane. If your source is in kilograms, switch the dropdown
                      to kilograms — leaving it on tonnes multiplies everything by 1,000.
                    </div>
                  )}
                  {wildlyLow && (
                    <div className="mt-2 text-[11px] text-warn">
                      Biggest capacity is only {(maxKg / 1000).toFixed(1)} t on a {capacityClassT} t crane —
                      check the units dropdown.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={commit} disabled={busy || parsed.rows.length === 0 || wildlyHigh}
              className="flex-1 justify-center">
              {busy ? <Loader2 size={14} className="animate-spin"/>
                : wildlyHigh ? "Fix the units first"
                : `Add ${parsed.rows.length} cells`}
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Forgiving grid parser. Returns rows + a preview summary; never throws.
function parseChart(raw: string, units: "t" | "kg") {
  const out = { rows: [] as { boom: number; radius: number; kg: number }[], booms: [] as number[], minR: 0, maxR: 0, error: "", warn: "" };
  const lines = raw.split("\n").map(l => l.trimEnd()).filter(l => l.trim() !== "");
  if (lines.length < 2) { out.error = "Need a header row of boom lengths and at least one radius row."; return out; }

  const split = (l: string) => l.split(/\t|,|\s{2,}| +/).map(s => s.trim()).filter(s => s !== "");
  const head = split(lines[0]);
  // The header may or may not carry a leading corner label — a leading token
  // that isn't a number means "corner cell", drop it.
  const booms = head.filter(h => !isNaN(Number(h))).map(Number);
  if (booms.length === 0) { out.error = "Couldn't read boom lengths from the first row."; return out; }
  out.booms = booms;

  const mult = units === "t" ? 1000 : 1;
  let skipped = 0;
  const radii: number[] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = split(lines[i]);
    if (parts.length < 2) continue;
    const radius = Number(parts[0]);
    if (isNaN(radius)) { skipped++; continue; }
    radii.push(radius);
    for (let c = 0; c < booms.length; c++) {
      const cellRaw = parts[c + 1];
      if (cellRaw === undefined) continue;
      const v = Number(cellRaw.replace(/[^0-9.]/g, ""));
      if (!cellRaw || isNaN(v) || v <= 0) continue;         // blank cell = not charted
      out.rows.push({ boom: booms[c], radius, kg: Math.round(v * mult) });
    }
  }
  if (radii.length) { out.minR = Math.min(...radii); out.maxR = Math.max(...radii); }
  if (out.rows.length === 0) out.error = "No capacity values found — check the columns line up.";
  if (skipped > 0) out.warn = `${skipped} row(s) skipped — first value wasn't a radius.`;
  // Cheap sanity net: capacity should fall as radius grows. Flag if it doesn't.
  const byBoom = new Map<number, { r: number; kg: number }[]>();
  out.rows.forEach(r => { const a = byBoom.get(r.boom) ?? []; a.push({ r: r.radius, kg: r.kg }); byBoom.set(r.boom, a); });
  let odd = 0;
  byBoom.forEach(a => {
    a.sort((x, y) => x.r - y.r);
    for (let i = 1; i < a.length; i++) if (a[i].kg > a[i - 1].kg * 1.05) odd++;
  });
  if (odd > 0) out.warn += `${out.warn ? " " : ""}${odd} cell(s) rise as radius grows — worth a second look before verifying.`;
  return out;
}

// =============================================================================
// Pack import — every telescope-combo section in one hit
// =============================================================================
// Pasting a 14-section LICCON chart one block at a time was the workflow,
// once ("14 single pastes took too long" — James, 10/09, the Beechmont
// LTM 1120 entry). A pack file is what Claude builds from a lift study's text
// layer: "-- Variant <label> --" headers, each followed by its own
// boom-lengths/radii grid. This reads the whole file, previews every section,
// and commits the lot in one pass. Everything outside the variant blocks
// (config facts, notes, the quick-option grid) is ignored, so the pack file
// stays human-readable. The labels come from the FILE, not from typing — which
// is also how a label typo stops happening.
function ImportPackModal({ configId, variantAxis, capacityClassT, onClose, onSaved }: {
  configId: string; variantAxis: string | null;
  capacityClassT: number | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [raw, setRaw] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [units, setUnits] = useState<"t" | "kg">("t");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pack = parsePack(raw, units);
  const allRows = pack.blocks.flatMap(b => b.rows);

  // Same units net as the single paste — biggest cell vs the crane's rating.
  const maxKg = allRows.length ? Math.max(...allRows.map(r => r.kg)) : 0;
  const ratedKg = capacityClassT ? capacityClassT * 1000 : null;
  const wildlyHigh = !!ratedKg && maxKg > ratedKg * 1.6;
  const wildlyLow = !!ratedKg && maxKg > 0 && maxKg < ratedKg * 0.05;
  // Multi-section pack into a config with no variant axis would silently
  // overwrite same boom+radius across sections — the exact trap the axis exists
  // to prevent. Refuse rather than mangle.
  const noAxis = !variantAxis && pack.blocks.length > 1;

  function pickFile(f: File | null | undefined) {
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setRaw(String(reader.result ?? ""));
    reader.readAsText(f);
  }

  async function commit() {
    if (allRows.length === 0 || wildlyHigh || noAxis || pack.errors.length > 0) return;
    setBusy(true); setErr(null);
    const payload = pack.blocks.flatMap(b => b.rows.map(r => ({
      crane_config_id: configId,
      boom_length_m: r.boom,
      radius_m: r.radius,
      variant: variantAxis ? b.variant : "",
      capacity_kg: r.kg,
    })));
    for (let i = 0; i < payload.length; i += 200) {
      const { error } = await supabase.from("crane_chart_points")
        .upsert(payload.slice(i, i + 200), { onConflict: "crane_config_id,boom_length_m,radius_m,variant" });
      if (error) { setBusy(false); setErr(error.message); return; }
    }
    setBusy(false);
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-[720px] my-6" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">Import a chart pack</div>
          <button onClick={onClose} type="button" className="text-text-muted hover:text-text"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] text-text-muted leading-relaxed">
            Loads a whole <b className="text-text">pack file</b> in one go — every section marked
            <span className="font-mono text-text"> -- Variant &lt;label&gt; --</span> with its grid underneath.
            That's the format of the paste packs Claude extracts from lift studies. Section labels come
            from the file, so nothing gets retyped. Existing cells at the same boom, radius and label
            are overwritten.
          </div>
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}

          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="block text-[12px] text-text-muted">Capacities are in
              <Select value={units} onChange={e => setUnits(e.target.value as any)}>
                <option value="t">tonnes</option>
                <option value="kg">kilograms</option>
              </Select>
            </label>
            <label className="flex items-center justify-center gap-2 rounded-md border border-line bg-ink px-3 py-2
                              text-[12px] text-text cursor-pointer hover:border-accent/50 transition">
              <Upload size={13}/>
              <span className="truncate">{fileName ?? "Choose the pack file (.txt)"}</span>
              <input type="file" accept=".txt,text/plain" className="hidden"
                onChange={e => { pickFile(e.target.files?.[0]); e.target.value = ""; }}/>
            </label>
          </div>

          <Textarea rows={6} value={raw} onChange={e => { setRaw(e.target.value); setFileName(null); }}
            placeholder={"…or paste the whole pack file here.\n\n-- Variant 0/0/0/46/92/92 --\n\t32.9\n4.5\t29.6\n5.0\t28.6"}
            className="font-mono text-[12px]"/>

          {raw.trim() && (
            <div className="border border-line rounded-md p-3 space-y-2">
              {pack.errors.map((e, i) => (
                <div key={i} className="text-[12px] text-danger">{e}</div>
              ))}
              {noAxis && (
                <div className="text-[12px] text-danger">
                  This file has {pack.blocks.length} sections but the configuration has no extra chart
                  axis, so they'd overwrite each other. Recreate the configuration with the extra chart
                  axis set (e.g. "Telescope combo (%)"), or paste a single section instead.
                </div>
              )}
              {pack.blocks.length > 0 && (
                <>
                  <div className="text-[12px] text-ok flex items-center gap-1.5">
                    <CheckCircle2 size={13}/> {pack.blocks.length} section{pack.blocks.length === 1 ? "" : "s"} ·
                    {" "}{allRows.length} cells total
                  </div>
                  <div className="divide-y divide-line/40">
                    {pack.blocks.map(b => (
                      <div key={b.variant} className="py-1 flex items-center gap-3 text-[11px] font-mono flex-wrap">
                        <span className="text-accent flex-1 min-w-[140px] truncate">{b.variant}</span>
                        <span className="text-text-muted">{b.rows.length} cells</span>
                        <span className="text-text-dim">booms {b.booms.join(", ")} m</span>
                        <span className="text-text-dim">
                          {Math.min(...b.rows.map(r => r.kg)).toLocaleString()}–{Math.max(...b.rows.map(r => r.kg)).toLocaleString()} kg
                        </span>
                      </div>
                    ))}
                  </div>
                  {pack.blocks.some(b => b.warn) && (
                    <div className="text-[11px] text-warn space-y-0.5">
                      {pack.blocks.filter(b => b.warn).map(b => <div key={b.variant}>{b.variant}: {b.warn}</div>)}
                    </div>
                  )}
                  {wildlyHigh && (
                    <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                      <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/>
                      <b>Units look wrong.</b> The biggest capacity here is {(maxKg / 1000).toLocaleString()} t
                      on a {capacityClassT} t crane. If the file is in kilograms, switch the dropdown.
                    </div>
                  )}
                  {wildlyLow && (
                    <div className="text-[11px] text-warn">
                      Biggest capacity is only {(maxKg / 1000).toFixed(1)} t on a {capacityClassT} t crane —
                      check the units dropdown.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={commit}
              disabled={busy || allRows.length === 0 || wildlyHigh || noAxis || pack.errors.length > 0}
              className="flex-1 justify-center">
              {busy ? <Loader2 size={14} className="animate-spin"/>
                : wildlyHigh ? "Fix the units first"
                : `Add ${allRows.length} cells across ${pack.blocks.length} section${pack.blocks.length === 1 ? "" : "s"}`}
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
          <div className="text-[11px] text-text-dim text-center">
            The chart still lands as a draft — check it against the source document, then verify.
          </div>
        </div>
      </div>
    </div>
  );
}

// Splits a pack file into its "-- Variant <label> --" blocks and runs each
// block's grid through the same forgiving parser as a single paste. Lines
// outside variant blocks (headers, config facts, "== OPTION ... ==" sections)
// are ignored. Never throws.
function parsePack(raw: string, units: "t" | "kg") {
  const out = {
    blocks: [] as { variant: string; rows: { boom: number; radius: number; kg: number }[]; booms: number[]; minR: number; maxR: number; warn: string }[],
    errors: [] as string[],
  };
  if (!raw.trim()) return out;

  const found: { variant: string; lines: string[] }[] = [];
  let current: { variant: string; lines: string[] } | null = null;
  for (const line of raw.split("\n")) {
    // "-- Variant 0/0/0/46/92/92  (boom 32.9 m, 5 parts of line) --"
    const m = line.match(/^\s*--\s*Variant\s+(.+?)\s*(?:\(.*)?(?:--\s*)?$/i);
    if (m) { current = { variant: m[1].replace(/--\s*$/, "").trim(), lines: [] }; found.push(current); continue; }
    if (/^\s*(--|==)/.test(line)) { current = null; continue; }  // any other divider ends the block
    if (current) current.lines.push(line);
  }

  if (found.length === 0) {
    out.errors.push('No "-- Variant <label> --" sections found. This importer reads pack files — for a single plain grid, use "Paste more" instead.');
    return out;
  }

  const seen = new Set<string>();
  for (const b of found) {
    if (!b.variant) { out.errors.push("A variant header has no label."); continue; }
    if (seen.has(b.variant)) { out.errors.push(`Section "${b.variant}" appears twice in the file — fix the file before importing.`); continue; }
    seen.add(b.variant);
    const parsed = parseChart(b.lines.join("\n"), units);
    if (parsed.error) { out.errors.push(`Section ${b.variant}: ${parsed.error}`); continue; }
    out.blocks.push({ variant: b.variant, rows: parsed.rows, booms: parsed.booms, minR: parsed.minR, maxR: parsed.maxR, warn: parsed.warn });
  }
  return out;
}

// =============================================================================
// Add model / add configuration
// =============================================================================
function AddModelModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [make, setMake] = useState(""); const [model, setModel] = useState("");
  const [type, setType] = useState("mobile"); const [cls, setCls] = useState("");
  const [notes, setNotes] = useState(""); const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true); setErr(null);
    const { error } = await supabase.from("crane_models").insert({
      make: make.trim(), model: model.trim(), crane_type: type,
      capacity_class_t: cls ? Number(cls) : null, notes: notes.trim() || null,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }

  return (
    <ModalShell title="Add a crane" onClose={onClose}>
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">Make
          <Input value={make} onChange={e => setMake(e.target.value)} placeholder="Liebherr"/>
        </label>
        <label className="block text-[12px] text-text-muted">Model
          <Input value={model} onChange={e => setModel(e.target.value)} placeholder="LTM 1100-4.2"/>
        </label>
        <label className="block text-[12px] text-text-muted">Type
          <Select value={type} onChange={e => setType(e.target.value)}>
            <option value="mobile">Mobile / all-terrain</option>
            <option value="franna">Franna / pick-and-carry</option>
            <option value="crawler">Crawler</option>
            <option value="tower">Tower</option>
            <option value="other">Other</option>
          </Select>
        </label>
        <label className="block text-[12px] text-text-muted">Capacity class (t)
          <Input value={cls} onChange={e => setCls(e.target.value)} placeholder="100"/>
        </label>
      </div>
      <label className="block text-[12px] text-text-muted">Notes
        <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Where we've used it, who hires it to us"/>
      </label>
      <Button onClick={save} disabled={busy || !make.trim() || !model.trim()} className="w-full justify-center">
        {busy ? <Loader2 size={14} className="animate-spin"/> : "Add crane"}
      </Button>
    </ModalShell>
  );
}

function AddConfigModal({ modelId, onClose, onSaved }: { modelId: string; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(""); const [mode, setMode] = useState("MAIN BOOM (T)");
  const [nameTouched, setNameTouched] = useState(false);
  const [cw, setCw] = useState(""); const [ow, setOw] = useState(""); const [ol, setOl] = useState("");
  const [winch, setWinch] = useState(""); const [wind, setWind] = useState("");
  const [src, setSrc] = useState(""); const [axis, setAxis] = useState("");
  const [basis, setBasis] = useState<"generic" | "machine">("generic");
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);

  // The name used to be a required blank field, so the save button sat there
  // disabled and silent while James assumed the greyed placeholders were real
  // values (01/08). Build the name from the setup instead — it's the same
  // convention we'd type by hand, and it means the button always works.
  const derivedName = useMemo(() => {
    const bits: string[] = [mode.trim() || "Main boom"];
    if (cw) bits.push(`${cw}t CW`);
    if (ow && ol) bits.push(`${ow} × ${ol}m outriggers`);
    bits.push(basis === "generic" ? "(generic)" : "(machine chart)");
    return bits.join(" · ");
  }, [mode, cw, ow, ol, basis]);
  const shownName = nameTouched ? name : derivedName;

  async function save() {
    setBusy(true); setErr(null);
    const { error } = await supabase.from("crane_configs").insert({
      crane_model_id: modelId,
      config_name: (shownName.trim() || derivedName).slice(0, 200),
      setup_mode: mode.trim() || null,
      counterweight_t: cw ? Number(cw) : null,
      outrigger_width_m: ow ? Number(ow) : null,
      outrigger_length_m: ol ? Number(ol) : null,
      main_winch_capacity_kg: winch ? Math.round(Number(winch) * 1000) : null,
      chart_wind_limit_ms: wind ? Number(wind) : null,
      variant_axis: axis.trim() || null,
      variant_label: axis.trim() ? axis.trim() : null,
      source_document: src.trim() || null,
      chart_basis: basis,
      status: "draft",
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onSaved();
  }

  return (
    <ModalShell title="Add a configuration" onClose={onClose} wide>
      <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] text-text-muted leading-relaxed">
        A chart belongs to a <b className="text-text">setup</b>, not a crane. The same LTM 1060-3.1 lifts
        8.5 t at 10 m on a 4.3 t counterweight and 16.0 t at 10 m on 12.8 t — so give each setup its own entry.
      </div>
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <label className="block text-[12px] text-text-muted">Configuration name
        <Input value={shownName}
          onChange={e => { setNameTouched(true); setName(e.target.value); }}/>
        {!nameTouched && (
          <span className="text-[11px] text-text-muted">Built from the setup below — type over it if you'd rather name it yourself.</span>
        )}
      </label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <label className="block text-[12px] text-text-muted">Setup mode
          <Input value={mode} onChange={e => setMode(e.target.value)}/>
        </label>
        <label className="block text-[12px] text-text-muted">Counterweight (t)
          <Input value={cw} onChange={e => setCw(e.target.value)} placeholder="e.g. 12.8"/>
        </label>
        <label className="block text-[12px] text-text-muted">Main winch (t)
          <Input value={winch} onChange={e => setWinch(e.target.value)} placeholder="e.g. 17.7 — blank = chart governs"/>
        </label>
        <label className="block text-[12px] text-text-muted">Outrigger width (m)
          <Input value={ow} onChange={e => setOw(e.target.value)} placeholder="e.g. 6.30"/>
        </label>
        <label className="block text-[12px] text-text-muted">Outrigger length (m)
          <Input value={ol} onChange={e => setOl(e.target.value)} placeholder="e.g. 7.35"/>
        </label>
        <label className="block text-[12px] text-text-muted">Chart wind limit (m/s)
          <Input value={wind} onChange={e => setWind(e.target.value)} placeholder="e.g. 9"/>
        </label>
      </div>
      <label className="block text-[12px] text-text-muted">Extra chart axis (optional)
        <Input value={axis} onChange={e => setAxis(e.target.value)} placeholder="Articulation angle (°) — Franna only"/>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">Chart basis
          <Select value={basis} onChange={e => setBasis(e.target.value as any)}>
            <option value="generic">Generic — manufacturer published</option>
            <option value="machine">Machine chart — the hired crane</option>
          </Select>
        </label>
        <label className="block text-[12px] text-text-muted">Source document
          <Input value={src} onChange={e => setSrc(e.target.value)} placeholder="e.g. Liebherr LTM 1090-4.2 product brochure"/>
        </label>
      </div>
      <div className="text-[11px] text-text-muted -mt-1">
        Generic is the right choice for planning and quoting. Swap it to the machine chart when the
        crane company sends theirs — that resets verification so it gets a fresh look.
      </div>
      <Button onClick={save} disabled={busy} className="w-full justify-center">
        {busy ? <Loader2 size={14} className="animate-spin"/> : "Add configuration"}
      </Button>
      <div className="text-[11px] text-text-muted text-center -mt-1">
        Saves as a draft. You paste the load chart in on the next screen, then verify it.
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, wide = false }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-[8vh]" onClick={onClose}>
      <div className={`bg-ink-card border border-line rounded-lg w-full ${wide ? "max-w-[640px]" : "max-w-[460px]"}`}
        onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">{title}</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}
